import React, { SyntheticEvent, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  Breadcrumbs,
  Link,
  ButtonBase,
  IconButton,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';

import AddIcon from '../../assets/images/add-icon-white.svg';
import ReloadIcon from '../../assets/images/reload.png';
import EditableTextField from '../../components/EditableTextField';
import AppTabs, { AppTab } from '../../components/AppTabs';
import { RootState } from '../../store/rootReducer';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(3)} ${theme.spacing(4.5)} ${theme.spacing(1.5)}`,
  },
  heading: {
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: '-4%',
    lineHeight: '24px',
  },
  separator: {
    marginLeft: `${theme.spacing(0.5)} !important`,
    marginRight: `${theme.spacing(0.5)} !important`,
  },
}));

const Header: React.FC<{
  projectId: string | number;
  projectName: string;
  recordingName: string;
  onRecordingNameUpdate: (updatedName: string) => void;
  onFlowNameUpdate: (updatedName: string) => void;
  activeTab: string;
  count: string;
  handleTabChange: (event: SyntheticEvent<Element, Event>, tab: string) => void;
  handleCreateFlowClick: () => void;
  createMode: number;
  onBack: () => void;
  handleReload: () => void;
}> = ({
  projectId,
  projectName,
  recordingName,
  onRecordingNameUpdate,
  onFlowNameUpdate,
  activeTab,
  count,
  handleTabChange,
  createMode,
  handleCreateFlowClick,
  onBack,
  handleReload,
}) => {
  const classes = useStyles();
  const newFlow = useSelector((state: RootState) => state.flow);
  const [showReload, setShowReload] = useState(true);
  const onReload = () => {
    setShowReload(false);
    handleReload();
  };
  return (
    <Box className={classes.root}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
      >
        <Box>
          {createMode !== 2 && (
            <RecordingBreadcrumb
              projectId={projectId}
              projectName={projectName}
              recordingName={recordingName}
              onRecordingNameUpdate={onRecordingNameUpdate}
            />
          )}
          {createMode === 2 && (
            <FlowBreadcrumb
              recordingName={recordingName}
              flowName={newFlow.workflow_name}
              onFlowNameUpdate={onFlowNameUpdate}
              onBack={onBack}
            />
          )}
        </Box>
        {createMode === 0 && (
          <Button
            startIcon={
              <img src={AddIcon} alt="create flow" width="14" height="14" />
            }
            variant="contained"
            color="secondary"
            sx={{
              padding: '10px 21px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
              textTransform: 'unset !important',
            }}
            onClick={handleCreateFlowClick}
            aria-label="Create New Flow"
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                lineHeight: '16px',
              }}
            >
              Create New Flow
            </Typography>
          </Button>
        )}
      </Box>
      {createMode !== 2 && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <AppTabs value={activeTab} onChange={handleTabChange}>
              <AppTab value="SNAPSHOTS" label="SNAPSHOTS" />
              {/* <AppTab disabled className="divider" icon={DividerIcon} />
              <AppTab value="FLOWS" label="FLOWS" /> */}
            </AppTabs>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Typography
              variant="body1"
              color="primary"
              sx={{ textTransform: 'capitalize' }}
            >
              {count} {activeTab.toLowerCase()} found
            </Typography>
            {showReload && (
              <Tooltip title={'Click to reload screenshots'}>
                <IconButton
                  sx={{ p: '2px' }}
                  aria-label="edit"
                  onClick={onReload}
                >
                  <img src={ReloadIcon} alt="edit" width="18" height="18" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const RecordingBreadcrumb: React.FC<{
  projectId: string | number;
  projectName: string;
  recordingName: string;
  onRecordingNameUpdate: (value: string) => void;
}> = ({ projectId, projectName, recordingName, onRecordingNameUpdate }) => {
  const classes = useStyles();
  return (
    <Breadcrumbs classes={{ separator: classes.separator }}>
      <Link
        color="inherit"
        to={`/project/${projectId}`}
        component={RouterLink}
        underline="none"
      >
        <Typography
          variant="body1"
          color="primary"
          sx={{ textTransform: 'capitalize' }}
        >
          {projectName}
        </Typography>
      </Link>
      <EditableTextField
        value={recordingName}
        onSubmit={onRecordingNameUpdate}
        className={classes.heading}
      />
    </Breadcrumbs>
  );
};

const FlowBreadcrumb: React.FC<{
  recordingName: string;
  flowName: string;
  onFlowNameUpdate: (value: string) => void;
  onBack: () => void;
}> = ({ flowName, recordingName, onFlowNameUpdate, onBack }) => {
  const classes = useStyles();
  return (
    <Breadcrumbs classes={{ separator: classes.separator }}>
      <Link
        color="inherit"
        component={ButtonBase}
        underline="none"
        onClick={onBack}
      >
        <Typography
          variant="body1"
          color="primary"
          sx={{ textTransform: 'capitalize' }}
        >
          {/* TODO: Need to confirm this (Project name/recording name) */}
          {recordingName}
        </Typography>
      </Link>
      <EditableTextField
        value={flowName}
        onSubmit={onFlowNameUpdate}
        className={classes.heading}
      />
    </Breadcrumbs>
  );
};

Header.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  projectName: PropTypes.string.isRequired,
  recordingName: PropTypes.string.isRequired,
  onRecordingNameUpdate: PropTypes.func.isRequired,
  activeTab: PropTypes.oneOf(['SNAPSHOTS', 'FLOWS']).isRequired,
  count: PropTypes.string.isRequired,
  createMode: PropTypes.oneOf([0, 1, 2]).isRequired,
  handleCreateFlowClick: PropTypes.func.isRequired,
};

Header.defaultProps = {
  activeTab: 'SNAPSHOTS',
};

export default Header;
